import React, { useEffect, useState } from "react";
import { Modal, Table, Progress, Tag, Descriptions, Space, Button, message, Typography } from "antd";
import { getReq, postReq } from "../api";
import dayjs from "dayjs";

const { Text, Title } = Typography;

export default function CampaignDetailsModal({ open, onClose, campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && campaignId) {
      fetchCampaignDetails();
    }
  }, [open, campaignId]);

  const fetchCampaignDetails = async () => {
    setLoading(true);
    try {
      const res = await getReq(`/common/campaigns/${campaignId}`, {});
      if (res.status === 1) {
        setData(res.data);
      } else {
        message.error("Failed to load campaign details");
      }
    } catch (error) {
      console.error(error);
      message.error("Error loading campaign details");
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <Modal open={open} onCancel={onClose} footer={null} title="Campaign Details" destroyOnClose>
        <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
      </Modal>
    );
  }

  const { campaign, recipients } = data;

  const getStatusTag = (status) => {
    const statusMap = {
      DRAFT: { color: "default", label: "Draft" },
      SCHEDULED: { color: "blue", label: "Scheduled" },
      RUNNING: { color: "orange", label: "Running" },
      COMPLETED: { color: "green", label: "Completed" },
      FAILED: { color: "red", label: "Failed" },
      PAUSED: { color: "warning", label: "Paused" },
    };
    const config = statusMap[status] || { color: "default", label: status };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const recipientStatusTag = (status) => {
    const map = {
      PENDING: <Tag color="orange">Pending</Tag>,
      SENT: <Tag color="green">Sent</Tag>,
      FAILED: <Tag color="red">Failed</Tag>,
    };
    return map[status] || <Tag>{status}</Tag>;
  };

  const columns = [
    {
      title: "Recipient Name",
      key: "name",
      render: (_, r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || r.email,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: recipientStatusTag,
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY HH:mm:ss") : "-"),
    },
    {
      title: "Error Log",
      dataIndex: "error",
      key: "error",
      render: (err) => (err ? <Text type="danger" style={{ fontSize: 12 }}>{err}</Text> : "-"),
    },
  ];

  const total = campaign.totalRecipients || 0;
  const processed = (campaign.sentCount || 0) + (campaign.failedCount || 0);
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", justifyContent: "space-between", width: "95%", alignItems: "center" }}>
          <span>Campaign: {campaign.name}</span>
          <Button onClick={fetchCampaignDetails} size="small">Refresh</Button>
        </div>
      }
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={900}
    >
      <div style={{ marginTop: 16 }}>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Campaign Status">{getStatusTag(campaign.status)}</Descriptions.Item>
          <Descriptions.Item label="Email Template">{campaign.templateId ? campaign.templateId.name : "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Scheduled At">{dayjs(campaign.scheduledAt).format("DD MMM YYYY HH:mm")}</Descriptions.Item>
          <Descriptions.Item label="Completed At">
            {campaign.completedAt ? dayjs(campaign.completedAt).format("DD MMM YYYY HH:mm") : "-"}
          </Descriptions.Item>
        </Descriptions>

        {/* Progress Section */}
        <div style={{ margin: "24px 0", display: "flex", alignItems: "center", gap: 32 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <Text strong>Delivery Progress ({processed} of {total} processed)</Text>
              <Text type="secondary">{percent}%</Text>
            </div>
            <Progress percent={percent} status={campaign.status === "FAILED" ? "exception" : "active"} />
          </div>
          <Space size="large">
            <div style={{ textAlign: "center" }}>
              <Title level={5} style={{ margin: 0, color: "#52c41a" }}>{campaign.sentCount || 0}</Title>
              <Text type="secondary">Successful</Text>
            </div>
            <div style={{ textAlign: "center" }}>
              <Title level={5} style={{ margin: 0, color: "#f5222d" }}>{campaign.failedCount || 0}</Title>
              <Text type="secondary">Failed</Text>
            </div>
          </Space>
        </div>

        {/* Recipients List */}
        <div style={{ marginTop: 16 }}>
          <Title level={5} style={{ marginBottom: 12 }}>Recipients Logs</Title>
          <Table
            dataSource={recipients}
            columns={columns}
            rowKey="_id"
            pagination={{ pageSize: 5 }}
            size="small"
            scroll={{ x: true }}
          />
        </div>
      </div>
    </Modal>
  );
}
